import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  TextInput, 
  toast,
  Select,
  DataTable,
  Switch
} from '@etherisc/ui-kit';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC, usePermissions } from '../../hooks/usePermissions';
import { useProducts } from '../../hooks/useProducts';
import { productService } from '../../services/productService';
import type { Product } from '../../types/product';
import type { ActionDefinition } from '../../utils/tableHelpers';
import { PermissionGate } from '../../components/PermissionGate';
import { 
  PRODUCT_STATUS_LABELS
} from '../../types/product';
import {
  EditIcon,
  TrashIcon,
  SearchIcon,
  FileTextIcon,
  FilterIcon,
  EyeIcon,
} from 'lucide-react';

// Import column patterns for consistent table styling
import { 
  textColumn,
  dateColumn,
  statusColumn,
  numberColumn,
} from '../../utils/columns';

export function ProductManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useRBAC();
  const { userPermissions } = usePermissions();
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const showError = (message: string, description?: string) => toast.error(message, { description });
  
  const {
    filteredProducts,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    viewAllOrgs,
    setViewAllOrgs,
    pagination,
    totalProducts,
    activeProducts,
    draftProducts,
    refreshProducts,
    fetchProducts,
    updateProductOptimistically,
    deleteProductOptimistically,
  } = useProducts();

  // Handle optimistic updates from navigation state
  useEffect(() => {
    const state = location.state as { updatedProduct?: Product } | null;
    if (state?.updatedProduct) {
      updateProductOptimistically(state.updatedProduct.id, state.updatedProduct);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, updateProductOptimistically, navigate, location.pathname]);

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    fetchProducts(1, filters, pagination.limit, viewAllOrgs);
  }, [filters, pagination.limit, viewAllOrgs, fetchProducts, setSearchQuery]);

  // Handle status filter
  const handleStatusFilter = useCallback((status: string) => {
    const newFilters = {
      ...filters,
      status: status === 'all' ? undefined : status as 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'DRAFT',
    };
    setFilters(newFilters);
    fetchProducts(1, newFilters, pagination.limit, viewAllOrgs);
  }, [filters, setFilters, fetchProducts, pagination.limit, viewAllOrgs]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setSearchQuery('');
    fetchProducts(1, emptyFilters, pagination.limit, viewAllOrgs);
  }, [setFilters, setSearchQuery, fetchProducts, pagination.limit, viewAllOrgs]);

  // Handle view all orgs toggle
  const handleViewAllOrgsToggle = useCallback((enabled: boolean) => {
    setViewAllOrgs(enabled);
    fetchProducts(1, filters, pagination.limit, enabled);
  }, [setViewAllOrgs, fetchProducts, filters, pagination.limit]);

  // Handle product actions
  const handleCreate = useCallback(() => {
    navigate('/admin/products/create');
  }, [navigate]);

  const handleView = useCallback((product: Product) => {
    navigate(`/admin/products/${product.id}`);
  }, [navigate]);

  const handleEdit = useCallback((product: Product) => {
    navigate(`/admin/products/${product.id}/edit`);
  }, [navigate]);

  const handleDelete = useCallback(async (product: Product) => {
    if (!confirm(`Are you sure you want to delete "${product.name}"?`)) {
      return;
    }

    try {
      await productService.deleteProduct(product.id);
      deleteProductOptimistically(product.id);
      showSuccess('Product Deleted', `"${product.name}" has been deleted successfully.`);
    } catch (error: unknown) {
      console.error('Error deleting product:', error);
      const errorMessage = (error as Error)?.message || 'Failed to delete product.';
      showError('Delete Failed', errorMessage);
    }
  }, [deleteProductOptimistically, showSuccess, showError]);

  // Define action buttons
  const actionDefinitions: ActionDefinition<Product>[] = [
    {
      id: 'view',
      label: 'View Product',
      icon: <EyeIcon size={16} />,
      onClick: handleView,
      intent: 'secondary',
    },
    {
      id: 'edit',
      label: 'Edit Product',
      icon: <EditIcon size={16} />,
      onClick: handleEdit,
      intent: 'secondary',
      hidden: (product) => {
        // Check if user has permission
        if (!userPermissions?.includes('products:update')) {
          return true;
        }
        
        // Regular users can only edit products from their own organization
        return !isAdmin() && product.organizationId !== user?.organization?.id;
      },
    },
    {
      id: 'delete',
      label: 'Delete Product',
      icon: <TrashIcon size={16} />,
      onClick: handleDelete,
      intent: 'danger',
      hidden: (product) => {
        // Admin users can delete all products
        if (isAdmin()) {
          return false;
        }
        
        // Check if user has permission
        if (!userPermissions?.includes('products:delete')) {
          return true;
        }
        
        // Regular users can only delete products from their own organization
        return product.organizationId !== user?.organization?.id;
      },
      disabled: (product) => !!(product.quotesCount && product.quotesCount > 0),
    },
  ];

  // Define columns using pattern-based helpers
  const columns = [
    // Product Name using textColumn pattern
    textColumn<Product>('name', {
      header: 'Product Name',
      maxLength: 30
    }),
    
    // Product Code using textColumn pattern
    textColumn<Product>('code', {
      header: 'Code',
      width: 120,
      className: 'font-mono text-sm'
    }),
    
    // Version using textColumn pattern
    textColumn<Product>('version', {
      header: 'Version',
      width: 100,
      emptyDisplay: '1.0.0'
    }),
    
    // Status using statusColumn pattern
    statusColumn<Product>('status', {
      header: 'Status',
      transformLabel: (status) => PRODUCT_STATUS_LABELS[status as keyof typeof PRODUCT_STATUS_LABELS] || status
    }),
    
    // Quotes Count using numberColumn pattern
    numberColumn<Product>('quotesCount', {
      header: 'Quotes',
      width: 100,
      emptyDisplay: '0'
    }),
    
    // Organization using textColumn pattern (always visible)
    textColumn<Product>('organization', {
      header: 'Organisation',
      getValue: (product) => (product as Product).organization?.name || 'Unknown',
      maxLength: 20
    }),
    
    // Created date using dateColumn pattern
    dateColumn<Product>('createdAt', {
      header: 'Created',
      format: 'relative'
    }),
    
    // Actions column
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableResizing: false,
      size: 140,
      cell: ({ row }: { row: { original: Product } }) => {
        const product = row.original;
        
        // Filter visible actions
        const visibleActions = actionDefinitions.filter(action => !action.hidden?.(product));
        
        return (
          <div className="flex items-center gap-2">
            {visibleActions.map((action) => (
              <button
                key={action.id}
                onClick={() => action.onClick(product)}
                disabled={action.disabled?.(product)}
                className={`p-1 rounded transition-colors ${
                  action.intent === 'danger' 
                    ? action.disabled?.(product) 
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-red-600 hover:text-red-800'
                    : action.disabled?.(product)
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-blue-600 hover:text-blue-800'
                }`}
                title={action.label}
              >
                {action.icon}
              </button>
            ))}
          </div>
        );
      },
    },
  ];

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">{error}</div>
        <NeutralButton onClick={refreshProducts}>
          Retry
        </NeutralButton>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileTextIcon size={24} />
            Product Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage insurance products and configurations
          </p>
        </div>

        <PermissionGate permission="products:create">
          <PositiveButton onClick={handleCreate}>
            <FileTextIcon size={16} />
            Create Product
          </PositiveButton>
        </PermissionGate>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-blue-600">{totalProducts}</div>
          <div className="text-sm text-gray-600">Total Products</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-green-600">{activeProducts}</div>
          <div className="text-sm text-gray-600">Active Products</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-orange-600">{draftProducts}</div>
          <div className="text-sm text-gray-600">Draft Products</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg border space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-700 font-medium">
            <FilterIcon size={18} />
            Filters
          </div>
          <div className="flex items-center gap-4">
            {isAdmin() && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={viewAllOrgs}
                  onCheckedChange={handleViewAllOrgsToggle}
                  id="view-all-orgs"
                />
                <label htmlFor="view-all-orgs" className="text-sm font-medium text-gray-700 cursor-pointer">
                  All Organisations
                </label>
              </div>
            )}
            <NeutralButton onClick={handleClearFilters}>
              Clear Filters
            </NeutralButton>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           {/* Search */}
           <div className="relative">
             <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 z-10" />
             <div className="pl-10">
               <TextInput
                 placeholder="Search products..."
                 value={searchQuery}
                 onChange={(e) => handleSearch(e.target.value)}
               />
             </div>
           </div>

           {/* Status Filter */}
           <div>
             <Select
               label="Status"
               options={[
                 { value: 'all', label: 'All Statuses' },
                 { value: 'ACTIVE', label: 'Active' },
                 { value: 'DRAFT', label: 'Draft' },
                 { value: 'PAUSED', label: 'Paused' },
                 { value: 'ARCHIVED', label: 'Archived' },
               ]}
               value={filters.status || 'all'}
               onValueChange={handleStatusFilter}
               placeholder="All Statuses"
             />
           </div>
        </div>
      </div>

      {/* Products Table */}
      <DataTable
        data={filteredProducts}
        columns={columns}
        loading={loading}
        manualPagination={true}
        pageCount={pagination.totalPages}
        rowCount={pagination.total}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1; // Convert 0-based to 1-based
          const newPageSize = paginationState.pageSize;
          
          fetchProducts(newPage, filters, newPageSize, viewAllOrgs);
        }}
        state={{
          pagination: {
            pageIndex: pagination.page - 1, // Convert 1-based to 0-based
            pageSize: pagination.limit,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
          enableFastNavigation: true,
          enableJumpToPage: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
      />
    </div>
  );
} 